import { yupResolver } from "@hookform/resolvers/yup";
import {
  AnalyticsComponentProvider,
  FormLayout,
  MultiTextField,
  type MultiTextFieldProps,
  PhoneField,
} from "@wayflyer/flyui";
import { UserDataSchemas } from "api-types";
import type { AxiosResponse } from "axios";
import type { FieldError } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useErrorModal } from "shared-ui";
import { getCookie } from "shared-utils";
import { useCustomerAppConfig } from "undercarriage";
import * as Yup from "yup";

import { useFundingApplication, useFundingApplicationMutation } from "../../queries/funding-application/queries";
import { useUserData, useUserDataMutation, useUserDataValidation } from "../../queries/user-data/queries";
import { COMMON_MESSAGES } from "../common-messages";

import { MESSAGES } from "./message-definitions";

// Should match CustomerFundingApplicationUpdate and CustomerFundingApplicationData section
export type ContactDetailsFormFields = {
  phoneNumber?: string;
  phoneRegion?: string;
  websiteUrls?: string[];
  hasAdditionalSites?: boolean;
};

export interface ContactDetailsEntryFormProps {
  onSubmit: (formData: ContactDetailsFormFields) => Promise<void>;
  initialValues: ContactDetailsFormFields;
  phoneNumberValidator: (
    number?: string,
    region?: string
  ) => Promise<AxiosResponse<UserDataSchemas.UserDataValidationResponse, any>>;
  formId: string;
}

const ContactDetailsEntryForm = ({
  onSubmit,
  initialValues,
  phoneNumberValidator,
  formId,
}: ContactDetailsEntryFormProps) => {
  const intl = useIntl();

  const validWebsiteMatcher =
    /^(((ftp|http|https):\/\/)?(www.)?(?!.*(ftp|http|https|www.))[a-zA-Z0-9_-]+(\.[a-zA-Z]+)+((\/)[\w#]+)*(\/\w+\?[a-zA-Z0-9_]+=\w+(&[a-zA-Z0-9_]+=\w+)*)?)?$/gm;

  const schema = Yup.object().shape({
    phoneNumber: Yup.string()
      .trim()
      .test(
        "Valid phone number - prefix combination",
        intl.formatMessage(MESSAGES.phoneNumberInvalid),
        async (value, context) => {
          // optional field
          if (!value) {
            return true;
          }
          const res = await phoneNumberValidator(value, context.parent.phoneRegion);
          return res.data.isValid;
        }
      ),
    phonePrefix: Yup.string().trim().optional(),
    contactPreference: Yup.string().trim().optional(),
    websiteUrls: Yup.array().of(
      Yup.string().matches(validWebsiteMatcher, intl.formatMessage(MESSAGES.invalidWebsiteUrl))
    ),
    hasAdditionalSites: Yup.bool().optional(),
  });

  const form = useForm<ContactDetailsFormFields>({
    resolver: yupResolver(schema),
    defaultValues: {
      ...initialValues,
    },
  });

  const {
    formState: { errors },
  } = form;

  const getWebsiteErrors = (errors: Array<FieldError>): Record<number, string> | undefined => {
    const websiteErrors: Record<number, string> = {};
    if (errors) {
      errors.forEach((item, index) => {
        if (item.message) {
          websiteErrors[index] = item.message;
        }
      });
    }
    return websiteErrors;
  };

  const websiteProps: MultiTextFieldProps = {
    addFieldLabel: intl.formatMessage(MESSAGES.addAnotherWebsiteLabel),
    textFieldProps: {
      label: intl.formatMessage(MESSAGES.websiteFieldLabel),
      name: "website-1",
      helperText: intl.formatMessage(MESSAGES.websiteFieldHelperText),
      defaultValue: initialValues?.websiteUrls?.[0],
    },
    additionalFields: [
      {
        name: "website-2",
        renderLabel: (fieldNumber) =>
          intl.formatMessage(MESSAGES.additionalWebsiteLabelDefinition, { number: fieldNumber - 1 }),
        defaultValue: initialValues?.websiteUrls?.[1],
      },
      {
        name: "website-3",
        renderLabel: (fieldNumber) =>
          intl.formatMessage(MESSAGES.additionalWebsiteLabelDefinition, { number: fieldNumber - 1 }),
        defaultValue: initialValues?.websiteUrls?.[2],
      },
    ],
    limitReachedRadioProps: {
      label: intl.formatMessage(MESSAGES.websiteLimitLabel),
      helperText: intl.formatMessage(MESSAGES.websiteLimitHelperText),
      name: "limit-reached",
      defaultValue: form.getValues("hasAdditionalSites") ? "yes" : "no",
      options: [
        { label: intl.formatMessage(COMMON_MESSAGES.yesLabel), value: "yes" },
        { label: intl.formatMessage(COMMON_MESSAGES.noLabel), value: "no" },
      ],
    },
    onChange: ({ hasAdditional, values }) => {
      form.setValue("hasAdditionalSites", hasAdditional);
      form.setValue("websiteUrls", values);
    },
    onBlur: () => {
      form.trigger("websiteUrls");
    },
    errors: getWebsiteErrors(errors.websiteUrls as Array<FieldError>),
  };

  const onDetailsSubmit = async (formData: ContactDetailsFormFields) => {
    await onSubmit(formData);
  };

  const phoneNumberInvalidError = form.getFieldState("phoneNumber", form.formState).error;

  return (
    <form id={formId} data-testid="contact-details-form" onSubmit={form.handleSubmit(onDetailsSubmit)}>
      <FormLayout>
        <PhoneField
          label={intl.formatMessage(MESSAGES.phoneNumberLabel)}
          helperText={intl.formatMessage(MESSAGES.phoneNumberHelperText)}
          onChange={({ regionCode, number }) => {
            form.setValue("phoneRegion", regionCode);
            form.setValue("phoneNumber", number);
          }}
          defaultNumber={initialValues?.phoneNumber}
          defaultRegion={initialValues?.phoneRegion}
          error={phoneNumberInvalidError?.message}
        />
        <MultiTextField {...websiteProps} />
      </FormLayout>
    </form>
  );
};

export interface ContactDetailsFormProps {
  onContinue: () => void;
  formId: string;
}

export const ContactDetailsForm = ({ onContinue, formId }: ContactDetailsFormProps) => {
  const {
    data: { customerDomain, countryOfIncorporation },
  } = useCustomerAppConfig();
  const { openErrorModal } = useErrorModal();
  const { data: fundingData } = useFundingApplication(customerDomain);
  const { data: userData, isFetched: isUserDataLoaded } = useUserData(customerDomain);
  const userDataMutation = useUserDataMutation(customerDomain);
  const contactDetailsMutation = useFundingApplicationMutation(customerDomain);
  const phoneNumberValidator = useUserDataValidation(customerDomain);
  const countryCookieData: string | undefined = getCookie("country-of-incorporation");

  const onContactDetailsSubmit = async (formData: ContactDetailsFormFields) => {
    if (!fundingData) {
      return;
    }
    try {
      const userData: UserDataSchemas.UserDataUpdate =
        formData.phoneNumber && formData.phoneRegion
          ? { contactPhoneNumber: { number: formData?.phoneNumber, regionCode: formData?.phoneRegion } }
          : {};
      await Promise.all([
        userDataMutation.mutateAsync(userData),
        contactDetailsMutation.mutateAsync({ ...formData, id: fundingData.id }),
      ]);
      onContinue();
    } catch (error) {
      openErrorModal({ variant: "unknown", error: error as Error });
    }
  };

  return isUserDataLoaded ? (
    <AnalyticsComponentProvider component="contact-details-form">
      <ContactDetailsEntryForm
        onSubmit={onContactDetailsSubmit}
        phoneNumberValidator={(number?: string, region?: string) =>
          phoneNumberValidator({
            contactPhoneNumber: number && region ? { number: number, regionCode: region } : undefined,
          })
        }
        initialValues={{
          ...fundingData,
          phoneNumber: userData?.contactPhoneNumber?.number,
          phoneRegion: userData?.contactPhoneNumber?.regionCode || countryOfIncorporation || countryCookieData,
        }}
        formId={formId}
      />
    </AnalyticsComponentProvider>
  ) : null;
};
